import * as Sentry from "@sentry/browser";
import React from "react";

import { APIError } from "../data/api";
import { toReportableRequest } from "../core/api_failure";
import { isChunkLoadError } from "../core/chunk_load_error";
import { Button } from "./button";
import { Container } from "./container";
import { Content } from "./content";
import { Row } from "./row";
import { Spacing } from "./spacing";
import { Text } from "./text";

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
  eventId: string;
}

interface FallbackContent {
  title: string;
  body: string;
  buttonTitle: string;
  buttonAppearance: "flat" | "full" | "outline";
  onButtonPress: () => void;
}

export class ErrorBoundary extends React.Component<{}, ErrorBoundaryState> {
  constructor(props: {}) {
    super(props);
    this.state = { hasError: false, isChunkError: false, eventId: "" };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // A well-formed API error is a server answering; the UI it drives is the
    // report. The error itself says whether it is the exception to that.
    if (error instanceof APIError && !error.reportable) {
      console.error(error.message);
      return;
    }

    Sentry.withScope((scope) => {
      scope.setExtras({ errorInfo });
      // A reported API error groups on the fault it names, so the request and
      // status behind it only reach the report as extras. Redacted rather than
      // raw: the manage-booking read names a signed token that grants amend and
      // cancel on the order, and a report is retained and widely readable.
      if (error instanceof APIError) {
        scope.setExtras({
          status: error.status,
          url: error.url && toReportableRequest(error.url),
        });
      }
      const eventId = Sentry.captureException(error);
      this.setState({ eventId });
    });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // A chunk-load failure that reaches here is the second miss: the auto-reload
    // already happened and still couldn't fetch the chunk. Offer a manual reload
    // (the asset may have finished propagating to the CDN) rather than a dead end.
    if (this.state.isChunkError) {
      return this.renderFallback(
        this.reloadFallback(
          "We couldn't finish loading this page",
          "A newer version may have just been released. Reloading should fix it."
        )
      );
    }

    // The report dialog is keyed on an event, so it is only offered once one
    // exists. Errors we deliberately don't report would otherwise open it on an
    // empty id and show the user a dead form.
    if (!this.state.eventId) {
      return this.renderFallback(
        this.reloadFallback(
          "Something went wrong :(",
          "Reloading should fix it. If it keeps happening, please get in touch."
        )
      );
    }

    return this.renderFallback({
      title: "Something went wrong :(",
      body: "This crash has been reported to us and we will work on resolving the issue as quickly as possible.",
      buttonTitle: "Report feedback",
      buttonAppearance: "outline",
      onButtonPress: () =>
        Sentry.showReportDialog({ eventId: this.state.eventId }),
    });
  }

  /** Copy varies; a reload is the only move the user has in either case. */
  private reloadFallback(title: string, body: string): FallbackContent {
    return {
      title,
      body,
      buttonTitle: "Reload page",
      buttonAppearance: "full",
      onButtonPress: () => window.location.reload(),
    };
  }

  private renderFallback(content: FallbackContent) {
    const { title, body, buttonTitle, buttonAppearance, onButtonPress } =
      content;
    return (
      <Content>
        <Container>
          <Spacing height={80} />
          <Text fontSize="lg" align="center">
            {title}
          </Text>
          <Spacing height={8} />
          <Text align="center">{body}</Text>
          <Spacing height={24} />
          <Row justifyContent="center">
            <Button
              title={buttonTitle}
              appearance={buttonAppearance}
              onPress={onButtonPress}
            />
          </Row>
        </Container>
      </Content>
    );
  }
}
